/* eslint-disable no-console */
import { useCallback, useEffect, useState } from "react";
import { esCotizable, esInstalando, generaComision } from "../../../constants/estadosEdificio";
import { EDIFICIOS_EXCLUIDOS } from "../../../constants/permisos";
import { getEdificios } from "../../../utils";

/**
 * Hook que gestiona el estado de edificios: totales de inventario, pantallas/audiencia
 * seleccionadas, nombres de edificios, y sincronización con inventarioData.
 */
export function useEdificiosData({ pais, inventarioData, includeInstallingBuildingsProp, isEditMode }) {
  // --- Estado: inventario total ---
  const [edificios, setEdificios] = useState([]);
  const [ascensoresTotal, setAscensoresTotal] = useState(0);
  const [totemsTotal, setTotemsTotal] = useState(0);
  const [coworkTotal, setCoworkTotal] = useState(0);
  const [esperaTotal, setEsperaTotal] = useState(0);

  // --- Estado: edificios seleccionados (conteo) ---
  const [edificiosSeleccionadosAscensores, setEdificiosSeleccionadosAscensores] = useState(0);
  const [edificiosSeleccionadosTotems, setEdificiosSeleccionadosTotems] = useState(0);
  const [edificiosSeleccionadosCowork, setEdificiosSeleccionadosCowork] = useState(0);
  const [edificiosSeleccionadosEspera, setEdificiosSeleccionadosEspera] = useState(0);
  const [edificiosUnicosTotal, setEdificiosUnicosTotal] = useState(0);

  // --- Estado: pantallas seleccionadas ---
  const [ascensoresSeleccionados, setAscensoresSeleccionados] = useState(0);
  const [totemsSeleccionados, setTotemsSeleccionados] = useState(0);
  const [coworkSeleccionados, setCoworkSeleccionados] = useState(0);
  const [esperaSeleccionados, setEsperaSeleccionados] = useState(0);

  // --- Estado: audiencia seleccionada ---
  const [audienciaSeleccionadaAscensor, setAudienciaSeleccionadaAscensor] = useState(0);
  const [audienciaSeleccionadaTotems, setAudienciaSeleccionadaTotems] = useState(0);
  const [audienciaSeleccionadaCowork, setAudienciaSeleccionadaCowork] = useState(0);
  const [audienciaSeleccionadaEspera, setAudienciaSeleccionadaEspera] = useState(0);
  const [audienciaSeleccionadaTotal, setAudienciaSeleccionadaTotal] = useState(0);

  // --- Estado: nombres de edificios ---
  const [nombresEdificiosSeleccionadosAscensores, setNombresEdificiosSeleccionadosAscensores] = useState([]);
  const [nombresEdificiosSeleccionadosTotems, setNombresEdificiosSeleccionadosTotems] = useState([]);
  const [nombresEdificiosSeleccionadosCowork, setNombresEdificiosSeleccionadosCowork] = useState([]);
  const [nombresEdificiosSeleccionadosEspera, setNombresEdificiosSeleccionadosEspera] = useState([]);
  const [nombresEdificiosNoSeleccionados, setNombresEdificiosNoSeleccionados] = useState([]);

  // --- Estado: flag edificios en instalación ---
  const [hasInstallingBuildings, setHasInstallingBuildings] = useState(false);
  const [includeInstallingBuildings, setIncludeInstallingBuildings] = useState(includeInstallingBuildingsProp);

  // Sincronizar prop con estado interno
  useEffect(() => {
    if (isEditMode) {
      setIncludeInstallingBuildings(includeInstallingBuildingsProp);
    }
  }, [includeInstallingBuildingsProp, isEditMode]);

  // Helper: validar estado de edificio según filtro
  const isValidState = useCallback(
    (edificio) => {
      if (!edificio.estado) return false;
      if (edificio.eliminada) return false;
      if (EDIFICIOS_EXCLUIDOS.includes(edificio.id)) return false;
      return includeInstallingBuildings ? esCotizable(edificio.estado) : generaComision(edificio.estado);
    },
    [includeInstallingBuildings]
  );

  // --- Sync: inventarioData → estados locales ---
  useEffect(() => {
    if (inventarioData) {
      setAscensoresSeleccionados(inventarioData.ascensoresSeleccionados || 0);
      setTotemsSeleccionados(inventarioData.totemsSeleccionados || 0);
      setCoworkSeleccionados(inventarioData.coworkSeleccionados || 0);
      setEsperaSeleccionados(inventarioData.esperaSeleccionados || 0);
      setAudienciaSeleccionadaAscensor(inventarioData.audienciaSeleccionadaAscensor || 0);
      setAudienciaSeleccionadaTotems(inventarioData.audienciaSeleccionadaTotems || 0);
      setAudienciaSeleccionadaCowork(inventarioData.audienciaSeleccionadaCowork || 0);
      setAudienciaSeleccionadaEspera(inventarioData.audienciaSeleccionadaEspera || 0);
      setAudienciaSeleccionadaTotal(inventarioData.audienciaSeleccionadaTotal || 0);
      setEdificiosSeleccionadosAscensores(inventarioData.edificiosSeleccionadosAscensores?.length || 0);
      setEdificiosSeleccionadosTotems(inventarioData.edificiosSeleccionadosTotems?.length || 0);
      setEdificiosSeleccionadosCowork(inventarioData.edificiosSeleccionadosCowork?.length || 0);
      setEdificiosSeleccionadosEspera(inventarioData.edificiosSeleccionadosEspera?.length || 0);

      const allEdificiosIds = [
        ...(inventarioData.edificiosSeleccionadosAscensores || []),
        ...(inventarioData.edificiosSeleccionadosTotems || []),
        ...(inventarioData.edificiosSeleccionadosCowork || []),
        ...(inventarioData.edificiosSeleccionadosEspera || []),
      ].filter(Boolean);
      const totalEdificiosUnicos = new Set(allEdificiosIds).size;
      setEdificiosUnicosTotal(totalEdificiosUnicos);
    }
  }, [inventarioData]);

  // --- Fetch: edificios para totales (solo cuando cambia pais o filtro de instalación) ---
  useEffect(() => {
    const fetchEdificios = async () => {
      try {
        const newEdificios = (await getEdificios({ pais })).filter((edificio) => !edificio.eliminada);
        const hasInstallation = newEdificios.some((edificio) => esInstalando(edificio.estado));
        setHasInstallingBuildings(hasInstallation);
        newEdificios.sort((a, b) => a.edificio.localeCompare(b.edificio));
        setEdificios(newEdificios);

        // Filtrado de edificios para totales
        const edificiosParaTotales = includeInstallingBuildings
          ? newEdificios
          : newEdificios.filter((edificio) => generaComision(edificio.estado));

        // Calcular totales
        setAscensoresTotal(edificiosParaTotales.reduce((sum, edificio) => sum + (edificio.pantallas || 0), 0));
        setTotemsTotal(edificiosParaTotales.reduce((sum, edificio) => sum + (edificio.totems || 0), 0));
        setCoworkTotal(edificiosParaTotales.reduce((sum, edificio) => sum + (edificio.cowork || 0), 0));
        setEsperaTotal(edificiosParaTotales.reduce((sum, edificio) => sum + (edificio.espera || 0), 0));
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error al obtener edificios:", error);
        }
      }
    };
    fetchEdificios();
  }, [pais, includeInstallingBuildings]);

  // --- Derivar nombres de edificios seleccionados (sin fetch, solo cálculo local) ---
  useEffect(() => {
    if (edificios.length === 0) return;

    const ascensoresIds = (inventarioData.edificiosSeleccionadosAscensores || []).map(Number);
    const totemsIds = (inventarioData.edificiosSeleccionadosTotems || []).map(Number);
    const coworkIds = (inventarioData.edificiosSeleccionadosCowork || []).map(Number);
    const esperaIds = (inventarioData.edificiosSeleccionadosEspera || []).map(Number);

    setNombresEdificiosSeleccionadosAscensores(
      edificios.filter((e) => ascensoresIds.includes(e.id) && isValidState(e)).map((e) => e.edificio)
    );
    setNombresEdificiosSeleccionadosTotems(
      edificios.filter((e) => totemsIds.includes(e.id) && isValidState(e)).map((e) => e.edificio)
    );
    setNombresEdificiosSeleccionadosCowork(
      edificios.filter((e) => coworkIds.includes(e.id) && isValidState(e)).map((e) => e.edificio)
    );
    setNombresEdificiosSeleccionadosEspera(
      edificios.filter((e) => esperaIds.includes(e.id) && isValidState(e)).map((e) => e.edificio)
    );
    setNombresEdificiosNoSeleccionados(
      edificios
        .filter((edificio) => {
          const isSelected =
            ascensoresIds.includes(edificio.id) ||
            totemsIds.includes(edificio.id) ||
            coworkIds.includes(edificio.id) ||
            esperaIds.includes(edificio.id);
          return !isSelected && isValidState(edificio);
        })
        .map((edificio) => edificio.edificio)
    );
  }, [inventarioData, edificios, isValidState]);

  return {
    // Inventario total
    edificios,
    ascensoresTotal,
    totemsTotal,
    coworkTotal,
    esperaTotal,
    // Edificios seleccionados (conteo)
    edificiosSeleccionadosAscensores,
    edificiosSeleccionadosTotems,
    edificiosSeleccionadosCowork,
    edificiosSeleccionadosEspera,
    edificiosUnicosTotal,
    // Pantallas seleccionadas
    ascensoresSeleccionados,
    totemsSeleccionados,
    coworkSeleccionados,
    esperaSeleccionados,
    // Audiencia seleccionada
    audienciaSeleccionadaAscensor,
    audienciaSeleccionadaTotems,
    audienciaSeleccionadaCowork,
    audienciaSeleccionadaEspera,
    audienciaSeleccionadaTotal,
    // Nombres de edificios
    nombresEdificiosSeleccionadosAscensores,
    nombresEdificiosSeleccionadosTotems,
    nombresEdificiosSeleccionadosCowork,
    nombresEdificiosSeleccionadosEspera,
    nombresEdificiosNoSeleccionados,
    // Flags
    hasInstallingBuildings,
    includeInstallingBuildings,
    setIncludeInstallingBuildings,
    // Setters expuestos para el useEffect de edit mode (index.js)
    setAscensoresSeleccionados,
    setTotemsSeleccionados,
    setCoworkSeleccionados,
    setEsperaSeleccionados,
    setAudienciaSeleccionadaAscensor,
    setAudienciaSeleccionadaTotems,
    setAudienciaSeleccionadaCowork,
    setAudienciaSeleccionadaEspera,
    setAudienciaSeleccionadaTotal,
    setEdificiosSeleccionadosAscensores,
    setEdificiosSeleccionadosTotems,
    setEdificiosSeleccionadosCowork,
    setEdificiosSeleccionadosEspera,
    setEdificiosUnicosTotal,
    // isValidState helper (para uso interno y externo)
    isValidState,
  };
}
